// 製造ステーション・使用設備・標準工数 sections of the BOM editor.
import { Plus, X, Factory, Clock, ArrowClockwise, Gear } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useT } from '@/i18n/LanguageContext';
import { EQUIPMENT_STATUS_STYLE } from '../EquipmentPickerDialog';

export const BomProcessFields = ({
  canWrite, workCenters, workCenterId, setWorkCenterId,
  equipment, equipmentIds, setEquipmentIds, bomEquipment, onOpenEqPicker,
  standardHours, setStandardHours, actualAvg, actualN, hourlyRate, setDirty,
}) => {
  const { t } = useT();
  const selected = equipmentIds
    .map((id) => equipment.find((e) => e.id === id) || bomEquipment.find((e) => e.id === id))
    .filter(Boolean);
  const rate = workCenters.find((w) => w.id === workCenterId)?.hourly_rate ?? hourlyRate;
  const sh = standardHours === '' ? null : Number(standardHours);
  return (
    <>
      {/* 製造ステーション (作業区) — where this item is made; auto-fills new work orders */}
      <div className="px-4 py-3 border-b border-zinc-200 bg-orange-50/30" data-testid="bom-station-field">
        <Label className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5"><Factory size={14} weight="duotone" className="text-orange-500" /> {t('mfg.bom.station')}</Label>
        <select data-testid="bom-station-select" value={workCenterId} disabled={!canWrite}
          onChange={(e) => { setWorkCenterId(e.target.value); setDirty(true); }}
          className="mt-1.5 h-9 w-full rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-800 disabled:opacity-60">
          <option value="">{t('mfg.bom.stationNone')}</option>
          {workCenters.map((w) => (
            <option key={w.id} value={w.id}>{w.name || w.code}{w.code && w.name ? ` (${w.code})` : ''}</option>
          ))}
        </select>
        <p className="text-[11px] text-zinc-400 mt-1">{t('mfg.bom.stationHint')}</p>
      </div>
      {/* 使用設備（機械） — machines used to build this item; distinct from the 作業区 above */}
      <div className="px-4 py-3 border-b border-zinc-200 bg-sky-50/30" data-testid="bom-equipment-field">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5"><Gear size={14} weight="duotone" className="text-sky-500" /> {t('mfg.bom.equipment')}</Label>
          {canWrite && equipment.length > 0 && (
            <Button size="sm" variant="ghost" data-testid="bom-equipment-add" onClick={onOpenEqPicker} className="h-7 text-sky-600">
              <Plus size={14} className="mr-1" /> {t('mfg.bom.equipmentAdd')}
            </Button>
          )}
        </div>
        {equipment.length === 0 && selected.length === 0 ? (
          <p className="text-[11px] text-zinc-400 mt-1.5" data-testid="bom-equipment-empty">{t('mfg.bom.equipmentNone')}</p>
        ) : selected.length === 0 ? (
          <p className="text-[11px] text-zinc-400 mt-1.5" data-testid="bom-equipment-none-selected">{t('mfg.bom.equipmentNoneSelected')}</p>
        ) : (
          <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
            {selected.map((e) => (
              <div key={e.id} data-testid={`bom-equipment-card-${e.id}`}
                className="flex items-center gap-2 rounded-lg border border-sky-200 bg-white px-2.5 py-2">
                <Gear size={16} weight="duotone" className="text-sky-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-zinc-800">{e.name}</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {e.asset_no && <span className="font-mono text-[10px] text-zinc-400">{e.asset_no}</span>}
                    {(e.status_label || e.status) && (
                      <span className={`rounded px-1 py-px text-[9px] font-medium ${EQUIPMENT_STATUS_STYLE[e.status] || 'bg-zinc-100 text-zinc-500'}`}>
                        {e.status_label || e.status}
                      </span>
                    )}
                  </div>
                </div>
                {canWrite && (
                  <button onClick={() => { setEquipmentIds((p) => p.filter((x) => x !== e.id)); setDirty(true); }}
                    data-testid={`bom-equipment-remove-${e.id}`} className="shrink-0 text-zinc-400 hover:text-rose-600"><X size={14} /></button>
                )}
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-zinc-400 mt-1.5">{t('mfg.bom.equipmentHint')}</p>
      </div>
      {/* 標準工数 (theoretical hours) vs 実績平均 (actual, from kiosk) */}
      <div className="px-4 py-3 border-b border-zinc-200 bg-indigo-50/30" data-testid="bom-standard-hours-field">
        <Label className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5"><Clock size={14} weight="duotone" className="text-indigo-500" /> {t('mfg.bom.standardHours')}</Label>
        <div className="flex items-center gap-2 mt-1.5">
          <Input type="number" step="0.1" min="0" data-testid="bom-standard-hours" value={standardHours} disabled={!canWrite}
            onChange={(e) => { setStandardHours(e.target.value); setDirty(true); }}
            placeholder="0.0" className="h-9 w-28 text-right" />
          <span className="text-sm text-zinc-500">{t('mfg.bom.hoursUnit')}</span>
          {actualAvg != null && (
            <span className="text-xs text-zinc-500 ml-2" data-testid="bom-actual-avg">
              {t('mfg.bom.actualAvg')}: <b className="text-indigo-600">{actualAvg} {t('mfg.bom.hoursUnit')}</b>
              <span className="text-zinc-400"> （{t('mfg.bom.sampleN', { n: actualN })}）</span>
            </span>
          )}
          {canWrite && actualAvg != null && (
            <Button type="button" size="sm" variant="ghost" data-testid="bom-apply-actual"
              onClick={() => { setStandardHours(String(actualAvg)); setDirty(true); }}
              className="h-7 text-indigo-600 ml-auto"><ArrowClockwise size={13} className="mr-1" /> {t('mfg.bom.applyActual')}</Button>
          )}
        </div>
        <p className="text-[11px] text-zinc-400 mt-1">{t('mfg.bom.standardHoursHint')}</p>
        {rate && sh ? (
          <p className="text-[11px] text-zinc-500 mt-1" data-testid="bom-standard-cost">{t('mfg.bom.standardCost')}: <b className="text-emerald-600">¥{Math.round(sh * rate).toLocaleString()}</b> <span className="text-zinc-400">（{sh} {t('mfg.bom.hoursUnit')} × ¥{Number(rate).toLocaleString()}/h）</span></p>
        ) : null}
      </div>
    </>
  );
};
